// frontend/src/components/Modals/InvoiceViewerModal.jsx
// 🆕 MODAL PARA VER DETALLES DE FACTURA

import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Row,
  Col,
  Card,
  CardBody,
  Badge,
  Table,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
  Alert
} from 'reactstrap';
import { invoicesAPI } from '../../services/api';
import { parseDate } from '../../utils/dates';

const InvoiceViewerModal = ({ isOpen, toggle, invoiceId }) => {
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('1');

  useEffect(() => {
    if (isOpen && invoiceId) {
      loadInvoice();
    }
  }, [isOpen, invoiceId]);

  const loadInvoice = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await invoicesAPI.getById(invoiceId);
      setInvoice(response.data);
    } catch (err) {
      console.error('Error cargando factura:', err);
      setError('Error al cargar la factura');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN'
    }).format(amount || 0);
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    return parseDate(date).toLocaleDateString('es-MX', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { color: 'warning', text: 'Pendiente', icon: 'ni-time-alarm' },
      paid: { color: 'success', text: 'Pagada', icon: 'ni-check-bold' },
      overdue: { color: 'danger', text: 'Vencida', icon: 'ni-fat-remove' },
      cancelled: { color: 'secondary', text: 'Cancelada', icon: 'ni-fat-remove' }
    };
    
    const config = statusConfig[status] || statusConfig.pending;
    
    return (
      <Badge color={config.color} className="badge-lg">
        <i className={`ni ${config.icon} me-1`}></i>
        {config.text}
      </Badge>
    );
  };

  const handleDownloadPDF = () => {
    if (invoice?.pdf_file) {
      window.open(invoice.pdf_file, '_blank');
    }
  };

  const handleDownloadXML = () => {
    if (invoice?.xml_file) {
      window.open(invoice.xml_file, '_blank');
    }
  };

  return (
    <Modal 
      isOpen={isOpen} 
      size="xl"
      className="modal-dialog-centered"
    >
      <ModalHeader toggle={toggle} className="bg-gradient-primary text-white">
        <i className="ni ni-single-copy-04 me-2"></i>
        Detalle de Factura {invoice?.folio && `- Folio ${invoice.folio}`}
      </ModalHeader>

      <ModalBody>
        {loading && (
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status">
              <span className="sr-only">Cargando...</span>
            </div>
          </div>
        )}

        {error && (
          <Alert color="danger">
            <span className="alert-icon"><i className="ni ni-support-16"></i></span>
            <span className="alert-text">{error}</span>
          </Alert>
        )}

        {invoice && !loading && (
          <>
            {/* Header con información principal */}
            <Row className="mb-4">
              <Col md="8">
                <h4 className="mb-2">
                  Factura {invoice.folio}
                  <span className="ms-3">{getStatusBadge(invoice.status)}</span>
                </h4>
                <p className="text-muted mb-1">
                  <strong>UUID:</strong> {invoice.uuid || 'N/A'}
                </p>
                <p className="text-muted mb-0">
                  <strong>Orden:</strong> {invoice.order_number} | 
                  <strong className="ms-2">Cliente:</strong> {invoice.client_name}
                </p>
              </Col>
              <Col md="4" className="text-md-end">
                <h3 className="text-success mb-2">{formatCurrency(invoice.total)}</h3>
                <p className="text-muted mb-0">
                  <small>Emitida: {formatDate(invoice.issue_date)}</small>
                </p>
                {invoice.due_date && (
                  <p className="text-muted mb-0">
                    <small>Vence: {formatDate(invoice.due_date)}</small>
                  </p>
                )}
              </Col>
            </Row>

            {/* Tabs de navegación */}
            <Nav tabs className="mb-3">
              <NavItem>
                <NavLink
                  className={activeTab === '1' ? 'active' : ''}
                  onClick={() => setActiveTab('1')}
                  style={{ cursor: 'pointer' }}
                >
                  <i className="ni ni-single-copy-04 me-1"></i>
                  Información General
                </NavLink>
              </NavItem>
              <NavItem>
                <NavLink
                  className={activeTab === '2' ? 'active' : ''}
                  onClick={() => setActiveTab('2')}
                  style={{ cursor: 'pointer' }}
                >
                  <i className="ni ni-bullet-list-67 me-1"></i>
                  Conceptos
                </NavLink>
              </NavItem>
              <NavItem>
                <NavLink
                  className={activeTab === '3' ? 'active' : ''}
                  onClick={() => setActiveTab('3')}
                  style={{ cursor: 'pointer' }}
                >
                  <i className="ni ni-credit-card me-1"></i>
                  Condiciones de Pago
                </NavLink>
              </NavItem>
            </Nav>

            {/* Contenido de los tabs */}
            <TabContent activeTab={activeTab}>
              {/* Tab 1: Información General */}
              <TabPane tabId="1">
                <Row>
                  <Col md="6">
                    <Card className="shadow-sm mb-3">
                      <CardBody>
                        <h6 className="text-primary mb-3">
                          <i className="ni ni-building me-2"></i>
                          Datos del Receptor
                        </h6>
                        <p className="mb-1">
                          <strong>Razón Social:</strong> {invoice.cliente_nombre}
                        </p>
                        <p className="mb-1">
                          <strong>RFC:</strong> {invoice.cliente_rfc}
                        </p>
                        <p className="mb-0">
                          <strong>Domicilio:</strong> {invoice.cliente_domicilio}
                        </p>
                      </CardBody>
                    </Card>
                  </Col>
                  <Col md="6">
                    <Card className="shadow-sm mb-3">
                      <CardBody>
                        <h6 className="text-primary mb-3">
                          <i className="ni ni-shop me-2"></i>
                          Datos del Emisor
                        </h6>
                        <p className="mb-1">
                          <strong>Razón Social:</strong> {invoice.emisor_nombre}
                        </p>
                        <p className="mb-1">
                          <strong>RFC:</strong> {invoice.emisor_rfc}
                        </p>
                        <p className="mb-0">
                          <strong>Domicilio:</strong> {invoice.emisor_domicilio}
                        </p>
                      </CardBody>
                    </Card>
                  </Col>
                </Row>

                {/* Montos */}
                <Card className="shadow-sm">
                  <CardBody>
                    <h6 className="text-primary mb-3">
                      <i className="ni ni-money-coins me-2"></i>
                      Desglose de Montos
                    </h6>
                    <Table borderless size="sm">
                      <tbody>
                        <tr>
                          <td width="70%">Subtotal:</td>
                          <td className="text-end">{formatCurrency(invoice.subtotal)}</td>
                        </tr>
                        {invoice.descuento > 0 && (
                          <tr>
                            <td>Descuento:</td>
                            <td className="text-end text-danger">
                              -{formatCurrency(invoice.descuento)}
                            </td>
                          </tr>
                        )}
                        <tr>
                          <td>IVA ({(invoice.iva_tasa * 100).toFixed(0)}%):</td>
                          <td className="text-end">{formatCurrency(invoice.iva_importe)}</td>
                        </tr>
                        <tr className="border-top">
                          <td className="font-weight-bold">Total:</td>
                          <td className="text-end font-weight-bold text-success">
                            {formatCurrency(invoice.total)}
                          </td>
                        </tr>
                      </tbody>
                    </Table>
                  </CardBody>
                </Card>
              </TabPane>

              {/* Tab 2: Conceptos */}
              <TabPane tabId="2">
                <Card className="shadow-sm">
                  <CardBody>
                    <h6 className="text-primary mb-3">
                      <i className="ni ni-bullet-list-67 me-2"></i>
                      Conceptos Facturados
                    </h6>
                    {invoice.conceptos && invoice.conceptos.length > 0 ? (
                      <Table responsive striped>
                        <thead>
                          <tr>
                            <th>Cantidad</th>
                            <th>Descripción</th>
                            <th>P. Unitario</th>
                            <th>Importe</th>
                          </tr>
                        </thead>
                        <tbody>
                          {invoice.conceptos.map((concepto, index) => (
                            <tr key={index}>
                              <td>{concepto.cantidad}</td>
                              <td>{concepto.descripcion}</td>
                              <td>{formatCurrency(concepto.valor_unitario)}</td>
                              <td>{formatCurrency(concepto.importe)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    ) : (
                      <p className="text-muted">No hay conceptos disponibles</p>
                    )}
                  </CardBody>
                </Card>
              </TabPane>

              {/* Tab 3: Condiciones de Pago */}
              <TabPane tabId="3">
                <Card className="shadow-sm">
                  <CardBody>
                    <h6 className="text-primary mb-3">
                      <i className="ni ni-credit-card me-2"></i>
                      Condiciones de Pago
                    </h6>
                    <Row>
                      <Col md="6">
                        <p className="mb-2">
                          <strong>Método de Pago:</strong> {invoice.metodo_pago}
                        </p>
                        <p className="mb-2">
                          <strong>Forma de Pago:</strong> {invoice.forma_pago}
                        </p>
                        <p className="mb-2">
                          <strong>Moneda:</strong> {invoice.moneda}
                        </p>
                      </Col>
                      <Col md="6">
                        <p className="mb-2">
                          <strong>Días de Crédito:</strong> {invoice.dias_credito || 0} días
                        </p>
                        <p className="mb-2">
                          <strong>Fecha de Vencimiento:</strong> {formatDate(invoice.due_date)}
                        </p>
                        {invoice.payment_date && (
                          <p className="mb-2">
                            <strong>Fecha de Pago:</strong> {formatDate(invoice.payment_date)}
                          </p>
                        )}
                      </Col>
                    </Row>
                    
                    {invoice.observaciones && (
                      <div className="mt-3">
                        <strong>Observaciones:</strong>
                        <p className="mb-0">{invoice.observaciones}</p>
                      </div>
                    )}
                  </CardBody>
                </Card>
              </TabPane>
            </TabContent>
          </>
        )}
      </ModalBody>

      <ModalFooter className="bg-light">
        <Button
          color="info"
          onClick={handleDownloadPDF}
          disabled={!invoice?.pdf_file}
        >
          <i className="ni ni-cloud-download-95 me-2"></i>
          Descargar PDF
        </Button>
        {invoice?.xml_file && (
          <Button
            color="warning"
            onClick={handleDownloadXML}
          >
            <i className="ni ni-cloud-download-95 me-2"></i>
            Descargar XML
          </Button>
        )}
        <Button color="secondary" onClick={toggle}>
          Cerrar
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default InvoiceViewerModal;
